// @ts-strict-ignore

import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { TelephonySipWiseEventHandler } from "rainbow-web-sdk/services/telephony/telephonySipWiseEventHandler";
import { CallEvents, CallRB, CallStatus, CallType } from "rainbow-web-sdk/models/call.model";
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { CliOption } from "rainbow-web-sdk/models/cli.model";
import anonymizer from 'rainbow-web-sdk/utils/anonymizer';
import { Service } from 'rainbow-web-sdk/services/service';

import type { TelephonyService } from "rainbow-web-sdk/services/telephony/telephony.service";
import { TELEPHONY_SVC } from "rainbow-web-sdk/services/telephony/telephony.service";
import type { WebrtcGatewayService } from 'rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service';
import { WEBRTC_GATEWAY_SVC } from 'rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { Bubble } from '../../models/bubble.model';
export const TELEPHONY_SVC_SIP_WISE = 'TelephonyServiceSipWise';
export class TelephonyServiceSipWise extends Service {

    private mainService: MainService = MainService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();

    private telephonyService?: TelephonyService = this.mainService.inject<TelephonyService>(TELEPHONY_SVC);
    private webrtcGatewayService?: WebrtcGatewayService = this.mainService.inject<WebrtcGatewayService>(WEBRTC_GATEWAY_SVC);

    private portalURL: string;
    private portalAdminURL: string;

    private settings: any = {};
    public routingData: { devicesList: any[]; ringingDestinations: any[]; alsoOfficePhoneRingEnabled: boolean; };
    private emergencyCallData: any = {};
    private monolineIncomingMode: boolean = false;
    private telephonyLinePolicy: string;

    public static getInstance() {
        return Service.create(TELEPHONY_SVC_SIP_WISE, TelephonyServiceSipWise);
    }

    public static build() {
        return new TelephonyServiceSipWise();
    }

    private constructor() { super(); }

    /**
     * Start service method
     * @public
     * @async
     */
    public async start(): Promise<void> {
        this.logger.info("[TelephonyServiceSipWise] [TEL] === STARTING ===");

        if (!this.telephonyService) throw new Error("TelephonyServiceSipWise failure - Telephony service is missing");
        if (!this.webrtcGatewayService) throw new Error("TelephonyServiceSipWise failure - webrtc Gateway service is missing");

        this.portalURL = config.restServerUrl + "/api/rainbow/voice/v1.0/";
        this.portalAdminURL = config.restServerUrl + "/api/rainbow/rvcpprovisioning/v1.0/";

        this.routingDataClear();
        this.emergencyCallDataClear();
        this.initializeSettings();
        this.logger.info("[TelephonyServiceSipWise] [TEL] === STARTED ===");
    }

    /**
     * Reset the sipwise routing data
     */
    public routingDataClear(): void {
        this.routingData = {
            devicesList: [],
            ringingDestinations: [],
            alsoOfficePhoneRingEnabled: true
        };
    }

    /**
     * Get the sipwise url
     * @returns {string} the sipwise portal url
     */
    public getPortalURL(): string {
        return this.portalURL;
    }

    /**
     *
     *
     * @returns {boolean} Return TRUE if the monoline mode is activated either for user or for PBX system;
     */
    public isMonolineIncomingModeActivated(): boolean {
        return this.monolineIncomingMode;
    }

    /**
     * getTelephonyLinePolicy
     * @returns {string} Returns the telephony line policy of the current user (used for hub systems only)
     * Possible values are "mono_line" / "multi_lines" / "multi_lines_plus"
     */
    public getTelephonyLinePolicy(): string {
        return this.telephonyLinePolicy;
    }

    /**
     * getDeviceWithType
     * @param {string} deviceType Device type
     * @returns {any} Found device with given type
     */
    public getDeviceWithType(deviceType: string): any {
        if (this.routingData.devicesList.length < 1) {
            this.logger.info("[TelephonyServiceSipWise] getDeviceWithType : Anomaly API getUserDevices not yet performed");
            return null;
        }
        if (deviceType !== "sip" && deviceType !== "webrtc" && deviceType !== "generic") {
            this.logger.info("[TelephonyServiceSipWise] getDeviceWithType : Anomaly wrong device type");
            return null;
        }
        return this.routingData.devicesList.find(device => device.type === deviceType);
    }

    /**
     * getCurrentDevice
     * @returns {any} Current device
     */
    public getCurrentDevice(): any {
        if (this.routingData.devicesList.length < 1) {
            this.logger.info("[TelephonyServiceSipWise] getCurrentDevice : Anomaly API getUserDevices not yet performed");
            return null;
        }
        const currentDevice = this.routingData.devicesList.find(elt => elt.main);
        if (currentDevice) { //CRRAINB-31749
            this.logger.info("[TelephonyServiceSipWise] getCurrentDevice current/main deviceId : " + currentDevice.deviceId
                + " / type : " + currentDevice.type + " / state : " + currentDevice.state);
            this.eventService.publish("ON_SIPWISE_ROUTING_EVENT", currentDevice.type, this.routingData);
            this.telephonyService.rxSubject.next({ name: "ON_CURRENT_DEVICE_STATE", data: currentDevice.state });
            if (currentDevice.state !== "inService") {
                this.logger.info("[TelephonyServiceSipWise] getCurrentDevice : WARNING current device not inService");
            }
            return currentDevice;
        }
        this.logger.info("[TelephonyServiceSipWise] getCurrentDevice anomaly - no current/main device");
        return null;
    }

    /**
     * setCurrentDevice
     * @param {string} currentDeviceId : Id of device to be setted as main/current device
     * broadcast evt with type of main device + devices list
     * @returns {boolean} true if ok
     */
    public setCurrentDevice(currentDeviceId: string): boolean {
        const device = this.routingData.devicesList.find(elt => elt.deviceId === currentDeviceId);

        if (device) {
            this.logger.info("[TelephonyServiceSipWise] setCurrentDevice for device: " + device.deviceId
                + " / type : " + device.type);

            if (device.state === "outOfService") {
                this.logger.error("[TelephonyServiceSipWise] setCurrentDevice anomaly - current device outOfService");
            }
            this.routingData.devicesList.forEach((elt) => { elt.main = false; });//1st reset all main
            device.main = true;//then set the current
            //deprecated when xmpp routingupdate managed
            //$rootScope.$broadcast("ON_SIPWISE_ROUTING_EVENT", this.routingData.devicesList[deviceIndex].type, this.routingData);
            return true;
        }
        this.logger.error("[TelephonyServiceSipWise] setCurrentDevice anomaly - current device not found / for device " + currentDeviceId);
        return false;
    }

    /**
     * setRingingDevice
     * @param {string} ringingDeviceId Ringing device identifier
     * @returns {string[]} List of ringing device ids
     */
    public setRingingDevice(ringingDeviceId: string): string[] {
        const destinations = [];
        const currentDevice = this.getCurrentDevice();
        if (!currentDevice || !ringingDeviceId) {
            this.logger.info("[TelephonyServiceSipWise] setRingingDevice : Anomaly no current device");
            return null;
        }
        //first set webrtc device as ringing device (always ringing)
        const webrtcDeviceId = this.getDeviceWithType("webrtc")?.deviceId;//the 1st one supposed to be the only one
        if (!webrtcDeviceId) {
            this.logger.info("[TelephonyServiceSipWise] setRingingDevice : Anomaly no webrtc device");
            return null;
        }
        destinations.push(webrtcDeviceId);

        //then add officePhone as necessary
        const officePhoneDeviceId = this.getDeviceWithType("sip")?.deviceId;//the 1st one supposed to be the only one
        if (officePhoneDeviceId && ringingDeviceId === officePhoneDeviceId) { //officePhone selected
            destinations.push(officePhoneDeviceId);
        }
        return destinations;
    }

    /**
     * putUserRoutingInfo
     * @param {string} currentDeviceId the current device (default for outgoing call)
     * @param {any[]} destinations list of configured destinations
     * @returns {Promise<void>} Status promise resolved when the handling is complete
     */
    public async putUserRoutingInfo(currentDeviceId: string, destinations: any[]): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] putUserRoutingInfo : currentId = " + currentDeviceId + " destinations = " + destinations);

            //reject if wrong param
            if (!currentDeviceId && !destinations) {
                const error = new Error("set routing failure - missing para");
                this.logger.error("[TelephonyServiceSipWise] putUserRoutingInfo anomaly: missing para");
                throw error;
            }

            const reqData: { currentDeviceId?: string; destinations?: any[]; } = {};
            if (currentDeviceId) {
                const device = this.routingData.devicesList.find(elt => elt.deviceId === currentDeviceId);
                if (device) {
                    this.logger.info("[TelephonyServiceSipWise] putUserRoutingInfo found device with state : " + device.state);
                    if (device.state === "outOfService") { //CRRAINB-38917 from now only block the explicite outOfservice but not the unknow state
                        const error = new Error("Anomaly putUserRoutingInfo - current device outOfService");
                        this.logger.error("[TelephonyServiceSipWise] putUserRoutingInfo anomaly - current device outOfService");
                        throw error;
                    }
                    reqData.currentDeviceId = currentDeviceId;
                } else {
                    const error = new Error("Anomaly putUserRoutingInfo - current device not found");
                    this.logger.error("[TelephonyServiceSipWise] putUserRoutingInfo anomaly - current device not found");
                    throw error;
                }
            }
            if (destinations && destinations.length >= 1) {
                //TBC add some destinations ctrl ??
                reqData.destinations = destinations;
            } else { //CRAINB-17477
                const implicitDestinations = this.setRingingDevice(currentDeviceId);
                if (implicitDestinations && implicitDestinations.length >= 1) {
                    reqData.destinations = implicitDestinations;
                }
            }

            const url = new URL(this.portalURL + "routing");
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(reqData);
            this.logger.info("[TelephonyServiceSipWise] putUserRoutingInfo PUT " + url);
            this.logger.info("[TelephonyServiceSipWise] putUserRoutingInfo data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] putUserRoutingInfo success ");
            //provisoire until xmpp++
            if (currentDeviceId) {
                this.setCurrentDevice(currentDeviceId);//set this device as main
            }
            this.patchNomadicData();//RQRAINB-2660
            this.eventService.publish("ON_SIPWISE_ROUTING_EVENT", this.getCurrentDevice().type, this.routingData);
            //provisoire until xmpp--

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'putUserRoutingInfo');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getUserRoutingInfo
     */
    public async getUserRoutingInfo(): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getUserRoutingInfo");
            if (this.routingData.devicesList.length < 1) {
                this.logger.info("[TelephonyServiceSipWise] getUserRoutingInfo : sipWise devices not yet avalaible");
                return;
            }

            const url = new URL(this.portalURL + "routing");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] routing GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getUserRoutingInfo success / status : " + response.statusText);
            const currentDeviceId = respData.currentDeviceId;
            const destinations = respData.destinations;//RQ4863
            if (currentDeviceId) {
                const device = this.routingData.devicesList.find(elt => elt.deviceId === currentDeviceId);
                if (device) {
                    if (device.state === "outOfService") {
                        const error = new Error("Anomaly getUserRoutingInfo - current device outOfService");
                        this.logger.error("[TelephonyServiceSipWise] [TEL][API] getUserRoutingInfo anomaly - current device outOfService");
                        throw (error);
                    } else {
                        this.logger.info("[TelephonyServiceSipWise] [TEL][API] getUserRoutingInfo current : " + currentDeviceId + " type = " + device.type);
                        if (!this.setCurrentDevice(currentDeviceId)) {
                            throw (new Error("Anomaly getUserRoutingInfo - setCurrentDevice error"));
                        }
                    }
                } else {
                    const error = new Error("Anomaly getUserRoutingInfo - current device not found");
                    this.logger.error("[TelephonyServiceSipWise] [TEL][API] getUserRoutingInfo anomaly - current not found");
                    throw (error);
                }
            } else {
                const error = new Error("Anomaly getUserRoutingInfo - no current device");
                this.logger.error("[TelephonyServiceSipWise] [TEL][API] getUserRoutingInfo anomaly - no current device");
                throw (error);
            }
            if (destinations) { //RQ4863
                this.routingData.ringingDestinations = destinations;
                this.logger.info("[TelephonyServiceSipWise] getUserRoutingInfo  / ringing destinations nb  = " + destinations.length);
                this.routingData.alsoOfficePhoneRingEnabled = (this.routingData.ringingDestinations.findIndex(elt => elt.type === "sip") !== -1);
            } else {
                const error = new Error("Anomaly getUserRoutingInfo - no destination");
                this.logger.error("[TelephonyServiceSipWise] getUserRoutingInfo anomaly - no destination ");
                throw (error);
            }
            this.patchNomadicData();//RQRAINB-2660
            this.eventService.publish("ON_SIPWISE_ROUTING_EVENT", this.getCurrentDevice().type, this.routingData);

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getUserRoutingInfo');
            this.logger.error(`[TelephonyServiceSipWise] [TEL][API] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * patchNomadicData
     * for sipwise webrtcgateway patch the nomadicObject for hacking others layer
     */
    public patchNomadicData(): void {
        //RQRAINB-2660++
        //hack nomadicObject for compatibility with non sipwise nomadic layers
        const current = this.getCurrentDevice();
        this.logger.info("[TelephonyServiceSipWise] patchNomadicData current type =  " + current.type);

        if (current && current.type === "webrtc") { //means sipwise webrtcgateway case
            this.telephonyService.nomadicObject.destination = this.webrtcGatewayService.getMyMediaPillarRemoteExtension();
            if (this.webrtcGatewayService.isMediaPillarAvailable()) {
                //this.telephonyService.nomadicObject.destination = current.deviceId;
                this.telephonyService.nomadicObject.featureActivated = true;
                this.telephonyService.nomadicObject.modeActivated = true;
                this.telephonyService.nomadicObject.makeCallInitiatorIsMain = false;
                this.telephonyService.nomadicObject.otherPhoneModeDenied = true;
            } else {
                //this.telephonyService.nomadicObject.destination = current.deviceId;
                this.telephonyService.nomadicObject.featureActivated = true;//?? check with PLE
                this.telephonyService.nomadicObject.modeActivated = false;//?? check with PLE
                this.telephonyService.nomadicObject.makeCallInitiatorIsMain = true;
            }
        } else {
            this.telephonyService.nomadicObject = {};
            this.telephonyService.nomadicObject.destination = "";
            this.telephonyService.nomadicObject.featureActivated = true;//?? check with PLE
            this.telephonyService.nomadicObject.modeActivated = false;//??
            this.telephonyService.nomadicObject.makeCallInitiatorIsMain = true;
        }

        this.logger.info("[TelephonyServiceSipWise] patchNomadicData nomadicObject (desti/modeAct/CallIsMain) = "
            + this.telephonyService.nomadicObject.destination + "/" + this.telephonyService.nomadicObject.modeActivated
            + "/" + this.telephonyService.nomadicObject.makeCallInitiatorIsMain);
        this.eventService.publish("ON_CALL_NOMADIC_EVENT", this.telephonyService.nomadicObject);
        //RQRAINB-2660--
    }

    /**
     * getUserDevices
     * Fill devices list in routing data
     */
    public async getUserDevices(): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getUserDevices");

            const url = new URL(this.portalURL + "devices");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] devices GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody;

            this.logger.info("[TelephonyServiceSipWise] getUserDevices success / status : " + response.statusText);

            respData.forEach((device) => {
                const sipDevice = {
                    type: device.type,
                    id: device.id,
                    deviceId: device.deviceId,
                    jid_wrg: "",
                    extension: "",
                    state: "",
                    main: false,
                    modules: device.modules
                };
                if (device.type === "webrtc") {
                    sipDevice.main = false; //as default until update TOBECHEKED !!!!!!!!
                    sipDevice.jid_wrg = device.jid_wrg;
                    this.routingData.devicesList.unshift(sipDevice);
                } else {
                    this.routingData.devicesList.push(sipDevice);
                }

                if (device.monolineIncomingMode) {
                    this.logger.info("[TelephonyServiceSipWise] [TEL][API] getUserDevices -- monolineIncomingMode is activated");
                    this.monolineIncomingMode = true;
                }
                this.telephonyLinePolicy = device.telephonyLinePolicy;
                this.logger.info("[TelephonyServiceSipWise] [TEL][API] getUserDevices : deviceId = " + device.deviceId + " type = " + device.type);
            });
            this.logger.debug("[TelephonyServiceSipWise] [TEL][API] getUserDevices -- telephonyLinePolicy: " + this.telephonyLinePolicy);

            if (this.routingData.devicesList.length >= 1) {
                this.logger.info("[TelephonyServiceSipWise] [TEL][API] getUserDevices  : " + this.routingData.devicesList.length + " device(s)");
                if (this.routingData.devicesList[0].type !== "webrtc") {
                    this.logger.error("[TelephonyServiceSipWise] getUserDevices anomaly - no webrtc device");
                }
            } else {
                const error = new Error("Anomaly getUserDevices - no device");
                this.logger.error("[TelephonyServiceSipWise] getUserDevices anomaly - no device");
                throw (error);
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'devices');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);

            //add case for token expiration, go back to login page
            if (error?.errorDetailsCode === 401210 || error?.errorDetailsCode === 401202) {
                this.eventService.publish("ON_LOGOUT_EVENT", null, true);
            }

            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                     CLI STUFF                       */
    /*************************************************************/
    // TODO: Use src/models/admin/user.js's model when it will be refactored as typescript class

    /**
     * getCliOptionsCurrent
     */
    public async getCliOptionsCurrent(): Promise<CliOption> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getCliOptionsCurrent");

            const url = new URL(this.portalURL + "cli-options/current");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getCliOptionsCurrent GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;
            this.logger.info("[TelephonyServiceSipWise] getCliOptionsCurrent success / status : " + response.statusText);
            const currentCliOption = CliOption.createRoutineFromData(respData);
            return currentCliOption;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getCliOptionsCurrent');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getCliOptionsAll
     */
    public async getCliOptionsAll(): Promise<CliOption[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getCliOptionsAll");

            const url = new URL(this.portalURL + "cli-options");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getCliOptionsAll GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getCliOptionsAll success / status : " + response.statusText);
            const cliOptions: CliOption[] = [];
            respData.forEach((cliOption) => {
                cliOptions.push(CliOption.createRoutineFromData(cliOption));
            });
            return cliOptions;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getCliOptionsAll');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async setCliOption(cliOption: CliOption) {

        if (!cliOption || !cliOption.policy) {
            const error = "wrong parameters";
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setCliOption');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
        try {
            const headers = this.authService.getPostHeader();
            const reqMethod = "PUT";
            const reqUrl = this.portalURL + "cli-options";
            this.logger.info("[TelephonyServiceSipWise] Set cli-options " + reqMethod + " " + reqUrl);

            const body = JSON.stringify(cliOption);
            const response: Response = await fetch(reqUrl, { method: reqMethod, headers, body });
            if (!response.ok) { throw response; }
            const newCliOption = (await response.json()).data;
            this.logger.debug(`[TelephonyServiceSipWise] Set cli-options -- SUCCESS -- `);
            return newCliOption;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'cli-options');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // TODO: Use src/models/admin/user.js's model when it will be refactored as typescript class

    public async getClirOption() {
        try {
            const headers = this.authService.getPostHeader();
            const reqMethod = "GET";
            const reqUrl = this.portalURL + "clir-options";
            this.logger.info("[TelephonyServiceSipWise] Get clir-options " + reqMethod + " " + reqUrl);

            const response: Response = await fetch(reqUrl, { method: reqMethod, headers });
            if (!response.ok) { throw response; }
            const clirActivated = (await response.json()).data.anonymous;
            this.logger.debug(`[TelephonyServiceSipWise] Get clir-options -- SUCCESS -- : clirActivateed :` + clirActivated);
            return clirActivated;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'clir-options');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async setClirOption(clirActivation: boolean) {

        try {
            const headers = this.authService.getPostHeader();
            const reqMethod = "PUT";
            const reqUrl = this.portalURL + "clir-options";
            this.logger.info("[TelephonyServiceSipWise] Set clir-options " + reqMethod + " " + reqUrl);

            const body = JSON.stringify({ anonymous: clirActivation });
            const response: Response = await fetch(reqUrl, { method: reqMethod, headers, body });
            if (!response.ok) { throw response; }
            const newClirOption = (await response.json()).data;
            this.logger.debug(`[TelephonyServiceSipWise] Set clir-options -- SUCCESS -- `);
            return newClirOption;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'clir-options');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                  GROUPS STUFF                     		 */
    /*************************************************************/
    /**
     * getUserGroups
     */
    public async getUserGroups(): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getUserGroups");

            const url = new URL(this.portalURL + "groups");
            url.searchParams.set("limit", String(100));

            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] groups GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getUserGroups success / status : " + response.statusText);

            this.telephonyService.sipWiseUserGroupsDataClear();//clear previous data
            if (respData.length > 0) {
                this.telephonyService.sipWiseUserGroupsData.myMemberId = this.contactService.userContact.dbId;

                respData.forEach((group) => {
                    this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.push(group);

                    const mngAssistAsHG = (group.type === "hunting_group" && group.subType === "manager_assistant");//RQRAINB-7479
                    if ((group.type === "hunting_group" && !mngAssistAsHG) // default hunting-group or attendant hunting-group
                        || group.type === "call_queue") { // call queue
                        this.telephonyService.sipWiseUserGroupsData.huntingGroupNb++;
                    }
                    if (mngAssistAsHG) { //RQRAINB-7479 new manager-assistant way (mng-assist as sub-type of hunting-group)
                        this.telephonyService.sipWiseUserGroupsData.isSomeMngAssistAsHG = true; //at least one mng-assist as HG
                        //this.telephonyService.sipWiseUserGroupsData.managerAssistantNb++;
                        const onlyManager = group.members.find(elt => elt.roles[0] === "leader");
                        if (onlyManager) {
                            if (group.members[0].memberId === this.contactService.userContact.dbId) { //if I'm the manager of this mng-assist group
                                group.members.forEach((member) => {
                                    if (member.roles[0] === "assistant") { //then add this assistant in my global assistant list
                                        this.telephonyService.sipWiseUserGroupsData.managerAssistantMyAssistList.push({ id: member.memberId, status: member.status });
                                    }
                                });
                            } else { //otherwise I'm necessary assistant for this group then add Mng in my global manager list
                                this.telephonyService.sipWiseUserGroupsData.managerAssistantMyMngList.push({ id: group.members[0].memberId, status: group.members[0].status });
                            }
                        } else {
                            this.logger.info("[TelephonyServiceSipWise] getUserGroups for groupId : " + group.id + " Anomaly not 1 only manager in 1st pos");
                        }
                    } else if (group.type === "manager_assistant") { //former manager-assistant way -- RQRAINB-7479
                        this.telephonyService.sipWiseUserGroupsData.managerAssistantNb++;
                        if (this.telephonyService.sipWiseUserGroupsData.managerAssistantNb === 1) {
                            //Only when 1:1 manager/assistant situation
                            const onlyManager = group.members.find(elt => elt.roles[0] === "manager");
                            if (onlyManager) {
                                this.telephonyService.sipWiseUserGroupsData.managerAssistGrId = group.id;
                                this.telephonyService.sipWiseUserGroupsData.managerId = onlyManager.memberId;
                                this.telephonyService.sipWiseUserGroupsData.managerName = onlyManager.displayName;
                                this.telephonyService.sipWiseUserGroupsData.managerStatus = onlyManager.status;
                            } else {
                                this.logger.info("[TelephonyServiceSipWise] getUserGroups for groupId : " + group.id + " Anomaly not 1 only manager");
                            }
                            const onlyAssistant = group.members.find(elt => elt.roles[0] === "assistant");
                            if (onlyAssistant) {
                                this.telephonyService.sipWiseUserGroupsData.assistantId = onlyAssistant.memberId;
                                this.telephonyService.sipWiseUserGroupsData.assistantName = onlyAssistant.displayName;
                                this.telephonyService.sipWiseUserGroupsData.assistantStatus = onlyAssistant.status;
                            } else {
                                this.logger.info("[TelephonyServiceSipWise] getUserGroups for groupId : " + group.id + " Anomaly not 1 only assistant");
                            }
                        } else {
                            this.logger.info("[TelephonyServiceSipWise] getUserGroups : Anomaly not a 1:1 mng/ass situation");
                        }
                    }
                });
            }
            if (this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length >= 1) {
                this.logger.info("[TelephonyServiceSipWise] getUserGroups  : " + this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length + " user's group(s)");
            } else {
                this.logger.info("[TelephonyServiceSipWise] getUserGroups  : no group");
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getUserGroups');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * joinGroup
     * @param {string} groupId group identifier
     * @param {string} leaderId leader identifier (for mng-assist GR)
     */
    public async joinGroup(groupId: string, leaderId: string = undefined): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] joinGroup for groupId : " + groupId + " leaderId:" + leaderId);
            let wrongParaMsg = "";
            let indexGroup = -1;
            if (groupId) {
                if (this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length === 0) {
                    wrongParaMsg = "no group";
                } else {
                    indexGroup = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.findIndex(elt => elt.id === groupId);
                    if (indexGroup === -1) {
                        wrongParaMsg = "group not found";
                    }
                }
            } else {
                wrongParaMsg = "missing groupId";
            }
            if (leaderId) {
                const found = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.find(group =>
                // eslint-disable-next-line @typescript-eslint/indent
                    (group.type === "hunting_group" && group.subType === "manager_assistant"
                    && group.members[0].memberId === leaderId)
                );
                if (!found) {
                    const errorMessage = "sipwise joinGroup -- invalid leaderId";
                    this.logger.error("[telephonyService] " + errorMessage);
                    return;
                }
            }

            //para checking to be completed
            if (wrongParaMsg !== "") { //reject if wrong parameters
                const error = new Error("join group failure - " + wrongParaMsg);
                this.logger.error("[TelephonyServiceSipWise] join group anomaly:" + wrongParaMsg);
                throw error;
            }
            //ICI
            let urlQueryParams = "";
            if (leaderId) {
                urlQueryParams = "?leaderId=" + leaderId;
            }
            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/join" + urlQueryParams);
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] joinGroup POST " + url);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] joinGroup success / status : " + response.statusText);
            //update sipWiseUserGroupsList
            indexGroup = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.findIndex(elt => elt.id === respData.id);
            if (indexGroup !== -1) {
                this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.splice(indexGroup, 1, respData);
            } else {
                this.logger.info("[TelephonyServiceSipWise] joinGroup  : anomaly group not present in local struct");
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'joinGroup');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * leaveGroup
     * @param {string} groupId group identifier
     * @param {string} leaderId leader identifier (for mng-assist GR)
     */
    public async leaveGroup(groupId: string, leaderId: string = undefined): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] leaveGroup for groupId : " + groupId + " leaderId:" + leaderId);
            let wrongParaMsg = "";
            let indexGroup = -1;
            if (groupId) {
                if (this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length === 0) {
                    wrongParaMsg = "no group";
                } else {
                    indexGroup = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.findIndex(elt => elt.id === groupId);
                    if (indexGroup === -1) {
                        wrongParaMsg = "group not found";
                    }
                }
            } else {
                wrongParaMsg = "missing groupId";
            }

            if (leaderId) {
                const found = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.find(group =>
                // eslint-disable-next-line @typescript-eslint/indent
                    (group.type === "hunting_group" && group.subType === "manager_assistant"
                    && group.members[0].memberId === leaderId)
                );
                if (!found) {
                    const errorMessage = "sipwise leaveGroup -- invalid leaderId";
                    this.logger.error("[telephonyService] " + errorMessage);
                    return;
                }
            }
            //para checking to be completed
            if (wrongParaMsg !== "") { //reject if wrong parameters
                const error = new Error("leave group failure - " + wrongParaMsg);
                this.logger.error("[TelephonyServiceSipWise] leave Group anomaly:" + wrongParaMsg);
                throw error;
            }
            //ICI
            let urlQueryParams = "";
            if (leaderId) {
                urlQueryParams = "?leaderId=" + leaderId;
            }
            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/leave" + urlQueryParams);
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] leave Group POST " + url);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] leave group success / status : " + response.statusText);

            //update sipWiseUserGroupsList
            indexGroup = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.findIndex(elt => elt.id === respData.id);
            if (indexGroup !== -1) {
                this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.splice(indexGroup, 1, respData);
            } else {
                this.logger.info("[TelephonyServiceSipWise] leaveGroup  : anomaly group not present in local struct");
            }
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'leaveGroup');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * joinAllGroups
     *  @param {string} subType group sub type (to target HG groups or mngAssist groups)
     */
    public async joinAllGroups(subType: string = "default"): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] joinAllGroups");
            let wrongParaMsg = "";

            if (this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length === 0) {
                wrongParaMsg = "no group";
            }

            if (subType !== "default" && subType !== "manager_assistant" && subType !== "hg_attendant") {
                wrongParaMsg = "wrong group subType";
            }

            //para checking to be completed
            if (wrongParaMsg !== "") { //reject if wrong situation
                const error = new Error("joinAllGroups failure - " + wrongParaMsg);
                this.logger.error("[TelephonyServiceSipWise] join all groups anomaly:" + wrongParaMsg);
                throw error;
            }
            let urlQueryParams = "";
            if (subType !== "default") {
                urlQueryParams = "?subType=" + subType;
            }
            const url = new URL(this.portalURL + "groups/join" + urlQueryParams);
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] joinAllGroups POST " + url);
            const response: Response = await fetch(url.toString(), { method: 'POST', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] joinAllGroups success / status : " + response.statusText);
            //update sipWiseUserGroupsList
            respData.forEach((group) => {
                const indexGroup = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.findIndex(elt => elt.id === group.id);
                if (indexGroup !== -1) {
                    this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.splice(indexGroup, 1, group);
                } else {
                    this.logger.info("[TelephonyServiceSipWise] joinAllGroups  : anomaly group not present in local struct");
                }
            });
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'joinAllGroups');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * @function leaveAllGroups
     * @public
     * @param {string} subType group sub type (to target HG groups or mngAssist groups)
     * @description leave all normal hunting group or leave the manager-assistant groups
     * @returns {Promise<any>} the API response
     */
    public async leaveAllGroups(subType: string = "default"): Promise<any> {
        try {
            this.logger.info("[TelephonyServiceSipWise] leaveAllGroups");
            let wrongParaMsg = "";

            if (this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length === 0) {
                wrongParaMsg = "no group";
            }

            if (subType !== "default" && subType !== "manager_assistant" && subType !== "hg_attendant") {
                wrongParaMsg = "wrong group subType";
            }
            //para checking to be completed
            if (wrongParaMsg !== "") { //reject if wrong situation
                const error = new Error("leaveAllGroups failure - " + wrongParaMsg);
                this.logger.error("[TelephonyServiceSipWise] leave all groups anomaly:" + wrongParaMsg);
                throw error;
            }

            const url = new URL(this.portalURL + "groups/leave");
            if (subType !== "default") {
                url.searchParams.set("subType", subType);
            }
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] leaveAllGroups POST " + url);
            const response: Response = await fetch(url.toString(), { method: 'POST', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] leaveAllGroups success / status : " + response.statusText);

            //update sipWiseUserGroupsList
            respData.forEach((group) => {
                const indexGroup = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.findIndex(elt => elt.id === group.id);
                if (indexGroup !== -1) {
                    this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.splice(indexGroup, 1, group);
                } else {
                    this.logger.info("[TelephonyServiceSipWise] leaveAllGroups  : anomaly group not present in local struct");
                }
            });
            return responseBody;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'leaveAllGroups');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * removeMemberFromGroup
     * @param {string} groupId group identifier
     * @param {string} memberId member identifier
     */
    public async removeMemberFromGroup(groupId: string, memberId: string): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] removeMemberFromGroup - groupId: " + groupId + " - memberId: " + memberId);

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/members/" + encodeURIComponent(memberId));
            const headers = this.authService.getPostHeader();

            const response: Response = await fetch(url.toString(), { method: 'DELETE', headers });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] removeMemberFromGroup success / status : " + response.statusText);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'removeMemberFromGroup');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * addMemberFromGroup
     * @param {string} groupId group identifier
     * @param {string} memberId member identifier
     */
    public async addMemberFromGroup(groupId: string, memberId: string): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] addMemberFromGroup - groupId: " + groupId + " - memberId: " + memberId);

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/members");
            const headers = this.authService.getPostHeader();
            const reqData = {
                memberId: memberId,
                status: "idle"
            };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] addMemberFromGroup success / status : " + response.statusText);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addMemberFromGroup');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * changeMemberRole
     * @param {string} groupId group identifier
     * @param {string} memberId member identifier
     * @param {boolean} isAgent
     * @param {boolean} isManager
     */
    public async changeMemberRole(groupId: string, memberId: string, isAgent: boolean, isManager: boolean): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] changeMemberRole - groupId: " + groupId + " - memberId: " + memberId + " - isAgent: " + isAgent.toString() + " - isManager: " + isManager.toString());
            const roles = [];
            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/members/" + encodeURIComponent(memberId));
            const headers = this.authService.getPostHeader();
            if (isAgent) {
                roles.push("agent");
            }
            if (isManager) {
                roles.push("manager");
            }
            const reqData = {
                roles: roles
            };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] DemoteMemberToAgent success / status : " + response.statusText);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'DemoteMemberToAgent');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * changeMemberStatus
     * @param {string} groupId group identifier
     * @param {string} memberId member identifier
     * @param {boolean} withdraw member is withdraw or not
     */
    public async changeMemberStatus(groupId: string, memberId: string, withdraw: boolean): Promise<void> {
        try {
            const status = withdraw ? "idle" : "active";
            this.logger.info("[TelephonyServiceSipWise] changeMemberStatus - groupId: " + groupId + " - memberId: " + memberId + " - status:" + status);

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/members/" + encodeURIComponent(memberId));
            const headers = this.authService.getPostHeader();
            const reqData = {
                status: status
            };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] PromoteMemberToManager success / status : " + response.statusText);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'PromoteMemberToManager');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * changeMemberOrder
     * @param {string} groupId group identifier
     * @param {string} memberId member identifier
     * @param {number} position position in the list
     */
    public async changeMemberOrder(groupId: string, memberId: string, position: number): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] changeMemberOrder - groupId: " + groupId + " - memberId: " + memberId + " - position:" + position.toString());

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/members/" + encodeURIComponent(memberId));
            const headers = this.authService.getPostHeader();
            const reqData = {
                position: position
            };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] changeMemberOrder success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'changeMemberOrder');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * setUserGroupForward
     * @param {string} groupId group identifier
     * @param {string} callForwardType forward type
     * @param {any} forwardData forward data
     */
    public async setUserGroupForward(groupId: string, callForwardType: string, forwardData: any): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] setUserGroupForward for groupId : " + groupId);
            let wrongParaMsg = "";
            if (groupId) {
                if (this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length === 0) {
                    wrongParaMsg = "invalid groupId";
                }
            } else {
                wrongParaMsg = "missing groupId";
            }
            //para checking to be completed
            if (wrongParaMsg !== "") { //reject if wrong parameters
                const error = new Error("set group forward failure - " + wrongParaMsg);
                this.logger.error("[TelephonyServiceSipWise] set group forward anomaly:" + wrongParaMsg);
                throw error;
            }
            //forwardData checking TO BE DONE

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/forwards/" + encodeURIComponent(callForwardType));
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(forwardData);
            this.logger.info("[TelephonyServiceSipWise] setUserGroupForward PUT " + url);
            this.logger.info("[TelephonyServiceSipWise] setUserGroupForward data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] setUserGroupForward success / status : " + response.statusText);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setUserGroupForward');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getUserGroupForwards
     * Retrieve the forwards for one given group of the logged user
     * @param {string} groupId group identifier
     */
    public async getUserGroupForwards(groupId: string): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getUserGroupForwards for groupId : " + groupId);
            let wrongParaMsg = "";
            if (groupId) {
                if (this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length === 0) {
                    wrongParaMsg = "invalid groupId";
                }
            } else {
                wrongParaMsg = "missing groupId";
            }
            //para checking to be completed
            if (wrongParaMsg !== "") { //reject if wrong parameters
                const error = new Error("get group forward failure - " + wrongParaMsg);
                this.logger.error("[TelephonyServiceSipWise] get group forward anomaly:" + wrongParaMsg);
                throw error;
            }

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/forwards");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getUserGroupForwards GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            const group = this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.find(elt => elt.id === groupId);
            if (group.name !== respData.name) {
                this.logger.info("[TelephonyServiceSipWise] getUserGroupForwards Anomaly group name : " + group.name + " VS " + respData.name);

                const errorInfo = await this.errorHelperService.getErrorInfo(response, 'getUserGroupForwards');
                this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
                throw errorInfo.error;
            }

            group.forwardData = respData;//data for complex n:m situation
            //only for simple 1:1 manager/assistant management++
            if (groupId === this.telephonyService.sipWiseUserGroupsData.managerAssistGrId) { //unique handled manager_assistant group
                if (this.telephonyService.sipWiseUserGroupsData.managerAssistantNb > 1) {
                    this.logger.info("[TelephonyServiceSipWise] getUserGroupForwards : Anomaly not a 1:1 mng/ass situation");
                } else {
                    let fwdActivate = "unknown";
                    const mng = respData.members.find(elt => elt.memberId === this.telephonyService.sipWiseUserGroupsData.managerId);
                    if (mng && mng.displayName === this.telephonyService.sipWiseUserGroupsData.managerName) {
                        const fwd = mng.forwards.find(elt => elt.type === "immediate");
                        if (fwd && fwd.activate && fwd.destinationType === "managersecretary") {
                            fwdActivate = "activated";
                        } else {
                            fwdActivate = "unactivated";
                        }
                    } else {
                        this.logger.info("[TelephonyServiceSipWise] getUserGroupForwards : Anomaly manager forward status");
                    }
                    this.telephonyService.sipWiseUserGroupsData.managerForward = fwdActivate;
                }
            }//only for simple 1:1 manager/assistant management--

            this.logger.info("[TelephonyServiceSipWise] getUserGroupForwards success / status : " + response.statusText);
            return group;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getUserGroupForwards');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getAllUserGroupsForwards
     *
     * Retrieve the forwards for all groups of logged user
     */
    public async getAllUserGroupsForwards(): Promise<void> {
        this.logger.info("[TelephonyServiceSipWise] getAllUserGroupsForwards");
        const getGroupPromises = [];
        if (this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.length === 0) {
            this.logger.info("[TelephonyServiceSipWise] getAllUserGroupsForwards / no group");
            return;
        }

        this.telephonyService.sipWiseUserGroupsData.sipWiseUserGroupsList.forEach((group) => {
            if (group.type === "manager_assistant") {
                getGroupPromises.push(this.getUserGroupForwards(group.id));
            }
        });

        if (getGroupPromises.length === 0) {
            this.logger.info("[TelephonyServiceSipWise] getAllUserGroupsForwards / no manager assistant group");
            return;
        }
        try {
            await Promise.all(getGroupPromises);
            this.logger.info("[TelephonyServiceSipWise] getAllUserGroupsForwards -- group forward completed -- " + getGroupPromises.length + " group(s) forward updated");
        } catch (error) {
            this.logger.error("[TelephonyServiceSipWise] getAllUserGroupsForwards -- group forward failure -- " + error.message);
            throw error;
        }
    }

    /**
     * getAllUserGroupsPhoneNumberDDI
     *
     * Retrieve the List DDI numbers associated to a CloudPBX
     */

    public async getAllUserGroupsPhoneNumberDDI(isAssignedToUser: boolean = false): Promise<any> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getAllUserGroupsPhoneNumberDDI");

            const url = new URL(this.portalURL + "phone-numbers/ddi");
            url.searchParams.set("isAssignedToUser", isAssignedToUser ? "true" : "false");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getAllUserGroupsPhoneNumberDDI GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAllUserGroupsPhoneNumberDDI');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getHuntingGroup
     * Get Group global information (including associated welcome services)
     * @param {string} groupId
     */
    public async getHuntingGroup(groupId: string): Promise<any> {
        try { //RQRAINB-9750
            this.logger.info("[TelephonyServiceSipWise] getHuntingGroup: " + groupId);

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId));
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] getHuntingGroup GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info("[TelephonyServiceSipWise] getHuntingGroup success");
            const responseBody: any = await response.json();
            const respData: any = responseBody.data;
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getHuntingGroup');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * updateHuntingGroup
     * @param {string} groupId
     * @param {string} externalNumberId
     */
    public async updateHuntingGroup(groupId: string, externalNumberId: string, callQueuePositionAnnounceEnabled?: boolean, callQueueWrapUpEnabled?: boolean, callQueueWrapUpTime?: number): Promise<any> {
        try {
            this.logger.info("[TelephonyServiceSipWise] updateHuntingGroup: " + groupId);

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId));

            const headers = this.authService.getPostHeader();
            const reqData = {};
            if (externalNumberId) {
                reqData['externalNumberId'] = externalNumberId;
            }
            if (callQueuePositionAnnounceEnabled !== undefined && callQueuePositionAnnounceEnabled !== null) {
                reqData['announceEnabled'] = callQueuePositionAnnounceEnabled;
            }

            if (callQueueWrapUpEnabled !== undefined && callQueueWrapUpEnabled !== null) { //RQRAINB-12482
                reqData['wrapUpEnabled'] = callQueueWrapUpEnabled;
            }

            if (callQueueWrapUpTime !== undefined && callQueueWrapUpTime >= 2 && callQueueWrapUpTime <= 900) { //RQRAINB-12482
                reqData['wrapUpTime'] = callQueueWrapUpTime;
            }

            const body = JSON.stringify(reqData);

            this.logger.info(`[TelephonyServiceSipWise] updateHuntingGroup PUT ${url} body ${body}`);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] updateHuntingGroup success");

            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateHuntingGroup');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * searchHuntingGroups
     * @param {string} searchText searched group name
     * @param {number} limit max number of results
     */
    public async searchHuntingGroups(searchText: string, limit: number = 20): Promise<any> {
        try {
            this.logger.info("[TelephonyServiceSipWise] searchHuntingGroups: " + anonymizer(searchText));

            // Check that SipWise telephony is running to avoid problem and throwing an error as portalURL undefined
            // If running, performed the request. Otherwise returns empty result array.
            if (!this.telephonyService || !this.telephonyService.isSipWise()) {
                this.logger.debug(`[TelephonyServiceSipWise] searchHuntingGroups -- Request ignored as SipWise telephony not running`);
                return [];
            }

            const url = new URL(this.portalURL + "search/huntinggroups");
            url.searchParams.set("name", searchText);

            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getUserGroupForwards GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] searchHuntingGroups success");
            const huntingGroups = respData.map((huntingGroup) => {
                const contact = this.contactService.createBasicContact(null, huntingGroup.shortNumber, false);
                contact.updateName(null, huntingGroup.name);
                contact.phoneInternalNumber = huntingGroup.shortNumber;
                //contact.phonePbx = huntingGroup.shortNumber;
                contact.phonePersoCan = huntingGroup.externalNumber ? huntingGroup.externalNumber : "";
                contact.dbId = huntingGroup.id;
                // contact.huntingGroupFlag = true;
                return contact;
            });
            huntingGroups.splice(limit);
            return huntingGroups;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchHuntingGroups');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * automatedAttendants list
     *
     */
    public async getAutomatedAttendantsList(): Promise<any> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getAutomatedAttendantsList");

            const url = new URL(this.portalURL + "auto-attendants");
            const automatedAttendants = [];
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getAutomatedAttendantsList GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getAutomatedAttendantsList success");
            if (respData && respData.length > 0) {
                respData.forEach((automatedAttendant) => {
                    automatedAttendants.push({ id: automatedAttendant.id, label: automatedAttendant.name });
                });
            }

            return automatedAttendants;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAutomatedAttendantsList');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // GET /api/rainbow/voice/v1.0/groups/:groupId/realtime-statistics
    public async getGroupRealtimeStatistics(groupId: string): Promise<any> {
        try {
            if (!groupId) return null;

            const url = new URL(this.portalURL + "groups/" + encodeURIComponent(groupId) + "/realtime-statistics");
            const headers = this.authService.getRequestHeader();
            const response: any = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const jsonResponse: any = await response.json();
            const data: any = jsonResponse.data;
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getGroupRealtimeStatistics');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }
    /*************************************************************/
    /*                  SNAPSHOT CALL STUFF                      */
    /*************************************************************/

    /**
     * getSnapshot
     * @param {Call} call Call for which the snapshot is requested
     * @param {string} deviceId Identifier of the device for which the snapshot is requested
     * @param {string} seqNum Sequence number for which the snapshot is requested
     */
    private async getSnapshot(call?: CallRB, deviceId?: string, seqNum?: string): Promise<any> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getSnapshot");

            const url = new URL(this.portalURL + "snapshot");
            if (call) {
                this.logger.info("[TelephonyServiceSipWise] snapshot with callId " + call.id);
                url.searchParams.set("callId", call.id);
            }
            if (deviceId) {
                this.logger.info("[TelephonyServiceSipWise] snapshot with deviceId " + deviceId);
                url.searchParams.set("deviceId", deviceId);
            }
            if (seqNum) {
                this.logger.info("[TelephonyServiceSipWise] snapshot with seqNum " + seqNum);
                url.searchParams.set("seqNum", seqNum);
            }

            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] snapshot GET " + url.toString());

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getSnapshot success / status : " + response.statusText);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'devices');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * processSnapshot
     */
    public async processSnapshot(): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] processSnapshot");
            const MAXSNAPCALLS = 10;
            const MAXSNAPRINGINGIN = 10;
            let nbRingingIn = 0;
            //getSnapshot : no filtering param
            const respData = await this.getSnapshot();

            if (!respData || !respData.devices) {
                this.logger.error("[TelephonyServiceSipWise] processSnapshot / anomaly no device ");
            } else { //update devices state
                respData.devices.forEach((snapDevice) => {
                    const state = (snapDevice.type !== "webrtc") ? snapDevice.state : "inService"; //use the given snapshot state except for webrtc device which is always inService
                    this.routingData.devicesList.find(device => device.deviceId === snapDevice.deviceId).state = snapDevice.state;
                    this.logger.info("[TelephonyServiceSipWise] processSnapshot / devicesId=" + snapDevice.deviceId + "; state=" + state);
                });
                this.routingData.devicesList[0].state = "inService";//whatever snapshot content force webrtc device state inService
                this.telephonyService.rxSubject.next({ name: "ON_CURRENT_DEVICE_STATE", data: this.getCurrentDevice().state }); //fix pb of device.state update in GUI when current is Sip
            }
            const createCallPromises = [];
            if (!respData || !respData.calls) {
                this.logger.info("[TelephonyServiceSipWise] processSnapshot / no call elt");
                // delete all client calls
                this.telephonyService.deleteCallsNotInList([]);
                return;
            }
            if (respData.calls.length > MAXSNAPCALLS) { //defense against call number anomaly
                this.logger.error("[TelephonyServiceSipWise] processSnapshot / anomaly to much calls : " + respData.calls.length);
                return;
            }
            respData.calls.forEach((snapCall) => {
                if (snapCall.legs[0].state === "ringingIn") { //defense against number of incoming ringing call anomaly
                    nbRingingIn++;
                    if (nbRingingIn >= MAXSNAPRINGINGIN) {
                        this.logger.info("[TelephonyServiceSipWise] processSnapshot / anomaly to much ringingIn calls : " + nbRingingIn);
                    }
                }
                if (nbRingingIn < MAXSNAPRINGINGIN) {
                    createCallPromises.push(this.createCallFromSnapShotCallsElt(snapCall));
                }
            });
            if (createCallPromises.length === 0) {
                this.logger.info("[TelephonyServiceSipWise] processSnapshot / anomaly no call created");
                return;
            }
            try {
                const discoveredCalls: CallRB[] = await Promise.all(createCallPromises);
                this.logger.info("[TelephonyServiceSipWise] processSnapshot -- calls creation completed -- " + createCallPromises.length + " calls created");

                // delete all client calls not present in discovered calls
                this.telephonyService.deleteCallsNotInList(discoveredCalls);
            }
            catch (error) {
                this.logger.error("[TelephonyServiceSipWise] processSnapshot -- calls creation failure -- " + error.message);
                throw error;
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'processSnapshot');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * createCallFromSnapShotCallsElt
     * @param {any} snapShotCallsElt Call element of snapshot response
     */
    private async createCallFromSnapShotCallsElt(snapShotCallsElt: any): Promise<CallRB> {
        try {
            //TBC++
            // Create the call object
            let call: CallRB;
            this.logger.info("[TelephonyServiceSipWise] createCallFromSnapShotCallsElt - callId = " + snapShotCallsElt.callId);
            //TBC :   provisory only endpoints[0] managed !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
            //note : in same call : multi legs => multi device // multi endpoints => conf "à n""
            let leg = null;
            let legId = null;
            let deviceId = null;
            let state = null;
            let endPoint = null;
            let phoneNumber = null;
            let jid = null;
            let displayName = null;

            if (snapShotCallsElt.legs) {
                const myMainDevice = this.getCurrentDevice();
                if (myMainDevice === null) {
                    const errorMessage = "createCallFromSnapShotCallsElt -- Anomaly no current device";
                    this.logger.error("[TelephonyServiceSipWise] " + errorMessage);
                    leg = snapShotCallsElt.legs[0]; //use leg[0] as default
                } else {
                    leg = snapShotCallsElt.legs.find((elt: any) => elt.deviceId === myMainDevice.id);
                    if (!leg) {
                        leg = snapShotCallsElt.legs[0]; //use leg[0] as default
                    }
                }
                legId = leg.legId ? leg.legId : null;
                deviceId = leg.deviceId ? leg.deviceId : null;
                state = (this.telephonyService.telephonyEventHandler as TelephonySipWiseEventHandler).convLegStateToCallStatus(leg.state);
                this.logger.info("[TelephonyServiceSipWise] createCallFromSnapShotCallsElt - legId = " + legId + " deviceId = " + deviceId + " leg_state = " + leg.state);
            } else {
                const errorMessage = "createCallFromSnapShotCallsElt -- Anomaly no leg";
                this.logger.error("[TelephonyServiceSipWise] " + errorMessage);
                return undefined;
            }
            if (snapShotCallsElt.endpoints) {
                endPoint = snapShotCallsElt.endpoints[0];
                phoneNumber = endPoint.phoneNumber;
                jid = endPoint.jid;
                displayName = endPoint.displayName;
            } else {
                this.logger.info("[TelephonyServiceSipWise] createCallFromSnapShotCallsElt -- No endPoint provided !");
                return undefined;
            }

            if (jid || phoneNumber) {
                const contact = await this.contactService.getOrCreateContact(jid, phoneNumber);
                if (!jid && !contact.displayName) { //only phone number and no contact.displayName then use this displayName ?? TO BE CHECKED
                    contact.displayName = displayName;
                    contact.initials = displayName.charAt(0);
                    contact.updateAvatar();
                }
                call = this.telephonyService.getOrCreateCallSipWise(state, snapShotCallsElt.callId, legId, deviceId, contact);
                call.relevantEquipmentId = deviceId;// = CallRB.getDeviceIdFromConnectionId(call.connectionId); ??? TBC
                call.isSecondary = this.getCurrentDevice() && this.getCurrentDevice().type === "webrtc";
                this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

                this.telephonyService.checkCallWithLostAudio(call);
                return call;
            }
            const errorMessage = "createCallFromSnapShotCallsElt -- Anomaly no jid no phoneNumber";
            this.logger.error("[TelephonyServiceSipWise] " + errorMessage);
            return undefined;//nothing to do or create an anonymous or reject ?
            //TBC--
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createCallFromSnapShotCallsElt');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                     WELCOME STUFF                         */
    /*************************************************************/
    /**
     * getWelcomeInfo
     * Get welcome global information
     * @param {string} targetId
     */
    public async getWelcomeInfo(targetId: string): Promise<any> {
        try { //RQRAINB-9750
            this.logger.info("[TelephonyServiceSipWise] getWelcomeInfo: for Id " + targetId);

            const url = new URL(this.portalURL + "ivrs/welcome-services/with-target/" + encodeURIComponent(targetId));
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] getWelcomeInfo GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info("[TelephonyServiceSipWise] getWelcomeInfo success");
            const responseBody: any = await response.json();
            const respData: any = responseBody.data;
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getWelcomeInfo');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                     FORWARD CALL STUFF                    */
    /*************************************************************/

    /**
     * FORWARD CALL sipWise to destination possible welcome
     * @param {string} destId the destination id (contact or hunting group)
     * @param {string} phoneNumber the destination of forward
     * @param {string} destType type of number (string) in [internalNumber, externalNumber, voicemail, welcome]
     * @param {string} forwardType the forward type (string) from [immediate, busy, noreply]
     * @param {string} [userId] id of the user for which the forward is modified (used by supervisors only). If not set, the user modified is the logged user
     * @function forwardToDeviceWithWelcome
     */
    public async forwardToDeviceWithWelcome(destId: string, phoneNumber: string, destType: string, forwardType: string, userId?: string): Promise<void> {
        try { //RQRAINB-9750++
            this.logger.info("[TelephonyServiceSipWise] forwardToDeviceWithWelcome : destId = " + destId + " / phoneNumber = " + phoneNumber + " / destType = " + destType + " / fwtype = " + forwardType);
            //reject if missing destination
            if (!phoneNumber && !destId) {
                const error = new Error("forward failure - missing phoneNumber");
                this.logger.error("[TelephonyServiceSipWise] forward anomaly: missing destination");
                throw error;
            }
            let welcomeId = null;
            if (destId && destType === "internalNumber") {
                const globalWelcomeInfo = await this.getWelcomeInfo(destId);
                if (globalWelcomeInfo?.length !== 0 && globalWelcomeInfo[0].id) {
                    welcomeId = globalWelcomeInfo[0].id;
                }
            }
            const url = new URL(this.portalURL + "forwards/" + encodeURIComponent(forwardType));
            if (userId) { url.searchParams.set("userId", userId); }

            const headers = this.authService.getPostHeader();
            const reqData: any = {
                destinationType: destType,
                activate: true
            };
            if (welcomeId) { reqData.welcomeServiceId = welcomeId; reqData.destinationType = "welcomeservice"; } else { reqData.number = phoneNumber; }

            const body = JSON.stringify(reqData);

            this.logger.info("[TelephonyServiceSipWise] forwardToDeviceWithWelcome PUT " + url);
            this.logger.info("[TelephonyServiceSipWise] forwardToDeviceWithWelcome data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }
            await response.json();

            this.logger.info("[TelephonyServiceSipWise] forwardToDeviceWithWelcome success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'forwardToDeviceWithWelcome');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }//RQRAINB-9750--

    /**
     * FORWARD CALL sipWise
     * @param {string} phoneNumber the destination of forward
     * @param {string} destType type of number (string) in [internalNumber, externalNumber, voicemail]
     * @param {string} forwardType the forward type (string) from [immediate, busy, noreply]
     * @param {string} [userId] id of the user for which the forward is modified (used by supervisors only). If not set, the user modified is the logged user
     */
    public async forwardToDevice(phoneNumber: string, destType: string, forwardType: string, userId?: string): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] forwardToDevice : phoneNumber = " + phoneNumber + " / destType = " + destType + " / fwtype = " + forwardType);
            //reject if missing destination
            if (!phoneNumber) {
                const error = new Error("forward failure - missing phoneNumber");
                this.logger.error("[TelephonyServiceSipWise] forward anomaly: missing phoneNumber");
                throw error;
            }

            const url = new URL(this.portalURL + "forwards/" + encodeURIComponent(forwardType));
            if (userId) { url.searchParams.set("userId", userId); }

            const headers = this.authService.getPostHeader();
            const reqData = {
                number: phoneNumber,
                destinationType: destType,
                activate: true
            };
            const body = JSON.stringify(reqData);

            this.logger.info("[TelephonyServiceSipWise] forwardToDevice PUT " + url);
            this.logger.info("[TelephonyServiceSipWise] forwardToDevice data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            await response.json();

            this.logger.info("[TelephonyServiceSipWise] forwardToDevice success");
            //provisory until xmpp evt ++
            //service.getForwardStatusSipWise(telService);
            //provisory until xmpp evt --
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'forwardToDevice');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * CANCEL FORWARD  sipWise
     * @param {string} forwardType the forward type (string) from [immediate, busy, noreply]
     * @param {string} userId id of the user for which the forward is cancelled (used by supervisors only). If not set, the user modified is the logged user
     */
    public async cancelForward(forwardType: string, userId?: string): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] cancelForward :  fwtype = " + forwardType);
            //reject if wrong forwardType
            if (!forwardType || (forwardType !== "immediate" && forwardType !== "busy" && forwardType !== "noreply")) {
                const error = new Error("cancel forward failure - wrong forwardType");
                this.logger.error("[TelephonyServiceSipWise] cancel forward anomaly: wrong forwardType");
                throw error;
            }

            const url = new URL(this.portalURL + "forwards/" + encodeURIComponent(forwardType));
            if (userId) { url.searchParams.set("userId", userId); }

            const headers = this.authService.getPostHeader();
            const reqData = {
                activate: false
            };
            const body = JSON.stringify(reqData);

            this.logger.info("[TelephonyServiceSipWise] cancelForward PUT " + url);
            this.logger.info("[TelephonyServiceSipWise] cancelForward data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            await response.json();

            this.logger.info("[TelephonyServiceSipWise] cancelForward success");
            //provisory until xmpp evt ++
            //service.getForwardStatusSipWise(telService);
            //provisory until xmpp evt --
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'cancelForward');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                 GET FORWARD STATUS STUFF                  */
    /*************************************************************/
    /**
     * 	 GET FORWARD STATUS sipWise
     */
    public async getForwardStatus(): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getForwardStatus");

            const url = new URL(this.portalURL + "forwards");

            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] forwards GET " + url.toString());

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            if (respData) {
                this.logger.info("[TelephonyServiceSipWise] getForwardStatus success");
                const forwardObjectAdapter = this.convForwardObject(respData);//former field adapter + new conditional Fwd
                this.logger.info("[TelephonyServiceSipWise] [TEL][API] getForwardStatus immediat fwdtype = " + forwardObjectAdapter.immediate.forwardType
                    + " immediat forwardTo = " + forwardObjectAdapter.immediate.forwardTo);
                if (forwardObjectAdapter.busy) {
                    this.logger.info("[TelephonyServiceSipWise] [TEL][API] getForwardStatus busy activate = " + forwardObjectAdapter.busy.activate);
                }
                if (forwardObjectAdapter.noreply) {
                    this.logger.info("[TelephonyServiceSipWise] [TEL][API] getForwardStatus noreply activate = " + forwardObjectAdapter.noreply.activate);
                }
                //update Fwd object
                this.telephonyService.forwardObject.forwardType = forwardObjectAdapter.immediate.forwardType;
                this.telephonyService.forwardObject.forwardTo = forwardObjectAdapter.immediate.forwardTo;
                this.telephonyService.forwardObject.pbxForwardType = "immediate";
                this.telephonyService.forwardObject.welcomeData = forwardObjectAdapter.immediate.welcomeData;//RQRAINB-9750
                this.telephonyService.forwardObject.destination = forwardObjectAdapter.immediate.destination;
                this.telephonyService.forwardObject.name = forwardObjectAdapter.immediate.name;

                //temporary until xmpp evt ++
                this.eventService.publish("ON_CALL_FORWARDED_EVENT", {
                    forwardType: forwardObjectAdapter.immediate.forwardType,
                    forwardTo: forwardObjectAdapter.immediate.forwardTo,
                    pbxForwardType: "immediate",
                    welcomeData: forwardObjectAdapter.immediate.welcomeData,
                    destination: forwardObjectAdapter.immediate.destination,
                    name: forwardObjectAdapter.immediate.name
                });

                if (forwardObjectAdapter.busy || forwardObjectAdapter.noreply) {
                    this.eventService.publish("ON_CALL_FORWARDED_CONDI_EVENT", {
                        busy: forwardObjectAdapter.busy,
                        noreply: forwardObjectAdapter.noreply
                    });
                    //update FWd object with new busy/noreply fields
                    this.telephonyService.forwardObject.busy = forwardObjectAdapter.busy;
                    this.telephonyService.forwardObject.noreply = forwardObjectAdapter.noreply;
                }
                //temporary until xmpp evt --
                return;
            }
            const error = new Error("getForwardStatus failure - invalide response");
            this.logger.error("[TelephonyServiceSipWise] getForwardStatus anomaly: invalide response");
            throw error;

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getForwardStatus');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * FORWARD helper method
     * forwardObject converter (from new forward API to former forwardObject structure)
     * @param {any} sipWiseForwardData Server Forward data
     * @returns {any} client forward object
     */
    public convForwardObject(sipWiseForwardData: any): any {
        const forwardAdapter = { //default
            immediate: { forwardType: "Deactivation", forwardTo: "", welcomeData: null, destination: "", name: "" },
            busy: { forwardType: "Deactivation", forwardTo: "", welcomeData: null },
            noreply: { forwardType: "Deactivation", forwardTo: "", welcomeData: null },
            manAssist: {}
        };
        sipWiseForwardData.forEach((forward) => {

            //immediate case
            if (forward.type === "immediate") {
                if (forward.activate) {
                    //RQRAINB-9750++
                    switch (forward.destinationType) {
                        case "voicemail":
                            forwardAdapter.immediate.forwardType = "Activation";
                            forwardAdapter.immediate.forwardTo = "VOICEMAILBOX";
                            break;
                        case "welcomeservice":
                            forwardAdapter.immediate.forwardType = "Activation";
                            forwardAdapter.immediate.forwardTo = forward.name;
                            forwardAdapter.immediate.welcomeData = { destinationId: forward.destinationId, name: forward.name };
                            break;
                        default:
                            if (forward.number !== "") {
                                forwardAdapter.immediate.forwardType = "Activation";
                                forwardAdapter.immediate.forwardTo = forward.number;
                                forwardAdapter.immediate.destination = forward.destination;
                                forwardAdapter.immediate.name = forward.name;
                            }
                            break;
                    }//RQRAINB-9750--
                }
                if (forward.destinationType === "managersecretary") {
                    forwardAdapter.immediate.forwardType = forward.activate;
                    forwardAdapter.immediate.forwardTo = "MANAGERASSISTANT";
                    forwardAdapter.manAssist = forward;
                }
            } else if (forward.type === "busy") { //busy case
                forwardAdapter.busy = forward;
                if (forward.destinationType === "welcomeservice") { //RQRAINB-9750
                    forwardAdapter.busy.welcomeData = { destinationId: forward.destinationId, name: forward.name };
                }
            } else if (forward.type === "noreply") { //noreply case
                forwardAdapter.noreply = forward;
                if (forward.destinationType === "welcomeservice") { //RQRAINB-9750
                    forwardAdapter.noreply.welcomeData = { destinationId: forward.destinationId, name: forward.name };
                }
            }
        });
        return forwardAdapter;
    }

    /*************************************************************/
    /*                 GET OVERFLOW STATUS STUFF                  */
    /*************************************************************/
    /**
     * GET OVERFLOW STATUS sipWise
     * @param {string} systemId system identifier
     * @param {string} phoneNumberId phone number identifier (subscrber identifier)
     */
    public async getOverFlowStatus(systemId: string, phoneNumberId: string): Promise<any> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getOverFlowStatus : systemId = " + systemId + " / phoneNumberId = " + phoneNumberId);

            const url = new URL(this.portalAdminURL + "cloudpbxs/" + encodeURIComponent(systemId) + "/subscribers/" + encodeURIComponent(phoneNumberId) + "/overflow/status");

            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getOverFlowStatus GET " + url.toString());

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;
            this.logger.info("[TelephonyServiceSipWise] getOverFlowStatus activated: " + respData.activate);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getOverFlowStatus');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Get the sipwise settings
     * @returns {any} the sipwise settings
     */
    public getSettings(): any {
        return this.settings;
    }

    /**
     * Initialization of SIP WISE SETTINGS
     * @returns {any} the sipwise settings
     */
    private async initializeSettings(): Promise<any> {
        this.logger.info("[TelephonyServiceSipWise] initializeSettings");

        try {
            const url = new URL(this.portalURL + "settings");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] initializeSettings GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const responseBody: any = await response.json();
            const data: any = responseBody.data;

            this.settings = data;
            this.logger.info("[TelephonyServiceSipWise] initializeSettings success / status : " + response.statusText + " - emulatedRingBackTone : " + data.emulatedRingBackTone);
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'initializeSettings');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                Emergency call STUFF                  	 */
    /*************************************************************/
    /**
     * emergencyCallDataClear
     * reset local cached emergency call data
     *
     */
    public emergencyCallDataClear(): void {
        this.emergencyCallData = {
            WGEmergencyCallForbidden: false,
            emergencyNuList: [],
            serverData: null //root server data
        };
    }

    /**
     * check if phoneNumber belongs to emergency numbers
     * @param {string} phoneNumber phone number to check
     * @returns {boolean} true if phoneNumber is an emergancy number
     */
    private checkEmergencyNumber(phoneNumber: string): boolean {
        return this.emergencyCallData && this.emergencyCallData.emergencyNuList.includes(phoneNumber);
    }

    /**
     * 	 Get Emergency numbers and emergency options
     */
    public async getEmergencyCallData(): Promise<void> {
        try {

            const url = new URL(this.portalURL + "emergency-numbers");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getEmergencyCallData GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getEmergencyCallDataSipWise success / status : " + response.statusText);
            this.logger.info("[TelephonyServiceSipWise] getEmergencyCallDataSipWise success / callAuthorizationWithSoftPhone  : " + respData.emergencyOptions.callAuthorizationWithSoftPhone);
            this.logger.info("[TelephonyServiceSipWise] getEmergencyCallDataSipWise success / nb of emergency Nu : " + respData.emergencyNumbers.length);
            this.emergencyCallDataClear();//1st clear previous data
            this.emergencyCallData.WGEmergencyCallForbidden = !respData.emergencyOptions.callAuthorizationWithSoftPhone;
            this.emergencyCallData.serverData = respData.emergencyNumbers; //root server data
            if (respData.emergencyNumbers && respData.emergencyNumbers.length > 0) {
                respData.emergencyNumbers.forEach((emergNumbers) => {
                    this.emergencyCallData.emergencyNuList.push(emergNumbers.number);
                });
            }
            if (this.telephonyService.sipWiseMode() === "testemergency") { //for test only
                this.emergencyCallData.WGEmergencyCallForbidden = true;
                this.emergencyCallData.emergencyNuList.push("3669");
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getEmergencyCallData');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                    MAKE CALL STUFF                        */
    /*************************************************************/

    public async makeSimpleCall(contact: Contact, phoneNumber: string, calledNumber?: string, bubble?: Bubble): Promise<string> {
        let call: CallRB;
        try {
            this.logger.info("[TelephonyServiceSipWise] [TEL] makeSimpleCall to " + (contact ? contact.nameForLogs : anonymizer(phoneNumber)));

            //reject not allowed operations
            if (!this.telephonyService.isBasicCallAllowed) {
                const profileError: any = new Error("makeSimpleCallSipWise failure - Not Allowed");
                profileError.status = profileError.errorDetailsCode = "403";
                this.logger.error("[TelephonyServiceSipWise] [TEL][API] makeSimpleCall " + profileError.message);

                // Release makingCall flag
                this.telephonyService.makingCall = false;
                throw profileError;
            }

            const phoneInfo = this.telephonyService.getPhoneInfo(contact, phoneNumber);
            //select current main device as default outgoing device
            const currentDevice = this.getCurrentDevice();
            const deviceId = currentDevice ? currentDevice.deviceId : "";

            //RQRAINB-3835++  manage barring for emergencyCall when webrtc
            if ((this.emergencyCallData.WGEmergencyCallForbidden && currentDevice && (currentDevice.type === "webrtc"))
                || (this.telephonyService.sipWiseMode() === "testemergency") //for test only with 3669
            ) {
                if (this.checkEmergencyNumber(phoneNumber)) {
                    this.logger.info("[TelephonyServiceSipWise] [TEL][API] makeVOIPSipemergencyCall - Not Allowed for num : " + phoneNumber);
                    this.telephonyService.rxSubject.next({ name: "ON_EMERGENCYCALL_VOIPSIP_NOTALLOWED_EVENT", data: phoneNumber });//subscribed at least in launcherService cf Mathieu
                    this.telephonyService.makingCall = false;
                    throw new Error();
                }
            }//RQRAINB-3835--

            const url = this.portalURL + "calls";
            const headers = this.authService.getPostHeader();
            const reqData: any = {
                deviceId: deviceId,
                calleeExtNumber: phoneInfo.longNumber,
                //calleeIntNumber: phoneInfo.internalNumber, no IntNumber in API
                calleeShortNumber: phoneInfo.shortNumber,
                calleePbxId: phoneInfo.pbxId,
                callerAutoAnswer: true,
                resource: this.xmppService.getResourceFromJid(this.xmppService.fullJid)
            };
            if (calledNumber) { reqData.calledNumber = calledNumber; }
            const body = JSON.stringify(reqData);

            this.logger.info("[TelephonyServiceSipWise] makeSimpleCall POST " + url);
            this.telephonyService.logMakecallReqData(reqData, contact);

            // call creation and storage in pendingMakecall data
            call = CallRB.create(CallStatus.ENDED, null, CallType.PHONE, contact);
            call.isOwner = true; // The web client is the owner of this call
            call.setIsVm(phoneNumber === this.telephonyService.voicemailNumber || phoneNumber === "voicebox"); // Indicate whether it is a call to own voicemail
            call.webConferenceRoom = bubble;
            //save pending created call
            this.telephonyService.pendingMakeCalls.push(call);
            this.logger.info("[TelephonyServiceSipWise] makeSimpleCall Call created (" + call + ")");

            // Send Makecall service request
            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }
            const responseBody: any = await response.json();
            const responseData: any = responseBody.data;

            const respCallId: string = responseData.callId;
            this.logger.info("[TelephonyServiceSipWise] [TEL][API] makeSimpleCall - success : callee " + anonymizer(phoneNumber) + " / callId = " + respCallId);

            if (!respCallId || respCallId === "") { //success but missing callId ???
                const call = CallRB.create(CallStatus.ERROR, null, CallType.PHONE, contact);
                call.errorMessage = "unexpectedError";
                this.telephonyService.calls[call.contact.id] = call;
                call.autoClear = setTimeout(() => { this.telephonyService.clearCall(call); }, 5000);
                // Release makinCall flag
                this.telephonyService.makingCall = false;
                // Send call update event
                this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

                const errorInfo = await this.errorHelperService.getErrorInfo(response, 'makeCall');
                this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
                throw errorInfo.error;
            }
            // Otherwise Create the call object (or retrieve it from pendingMakeCalls array)
            //At this stage legId & deviceId are unknown then set to null : if it is an issue use server obj link to read obj
            //const call = this.telephonyService.getOrCreateCallSipWise(CallStatus.DIALING, respCallId, null, null, contact);

            const isPendingMakeCall = this.telephonyService.pendingMakeCalls.includes(call);
            if (isPendingMakeCall) {
                // the created call is still pending : it means no csta event have been received for this call
                call.id = respCallId;
                call.setStatus(CallStatus.DIALING);
                call.startDate = call.startDate || new Date();

                call.setRelevantEquipmentId(call.getDeviceIdSipWise());//PROVISOIRE_SIPWISE

                // keepAlive defence : Arm keep alive TO in case of NGCP dead case for this call
                call.autoClear = setTimeout(() => { this.logger.info("[TelephonyServiceSipWise] keepAlive CallReset (" + call + ")"); this.telephonyService.clearCall(call); }, 10000);

                this.telephonyService.calls[call.id] = call;
                this.telephonyService.pendingMakeCalls.splice(this.telephonyService.pendingMakeCalls.indexOf(call), 1);
                this.logger.info("[telephonyService] makeSimpleCall Call confirmed by makecall acknowlegement (" + call + ")");

                // Send call update event
                this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
                return call.id;
            }

            // the created call is not pending anymore : it means some csta events have been received for this call
            return call.id ? call.id : respCallId;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'makeCall');
            this.logger.error(`[TelephonyServiceSipWise] [TEL][API] ${errorInfo.message}`);

            call.setStatus(CallStatus.ERROR);
            call.errorMessage = errorInfo.error.errorDetailsCode === "403" ? "notAllowed" : "invalidPhoneNumber";

            call.isSecondary = this.getCurrentDevice() && this.getCurrentDevice().type === "webrtc";//CRRAINB-40662

            this.telephonyService.calls[call.contact.id] = call;
            this.telephonyService.pendingMakeCalls.splice(this.telephonyService.pendingMakeCalls.indexOf(call), 1);

            call.autoClear = setTimeout(() => { this.telephonyService.clearCall(call); }, 5000);

            // Send call update event
            call.sendEvent(CallEvents.ON_CALL_UPDATED_EVENT);
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

            throw errorInfo.error;
        }
        finally {
            // Release makingCall flag
            this.telephonyService.makingCall = false;
        }
    }

    /*************************************************************/
    /*                    RELEASE CALL STUFF                     */
    /*************************************************************/

    /**
     * RELEASE SIPWISE CALL
     * @param {Call} call call to release
     * @param {string} reason Optional reason of call releasing, currently the only accepted value  = reject
     */
    public async releaseCall(call: CallRB, reason?: string): Promise<CallRB> {
        try {
            this.logger.info("[TelephonyServiceSipWise] [TEL][API] releaseCall " + call.id);

            let url = this.portalURL + "calls/" + encodeURIComponent(call.id);
            if (reason === "reject") { //RQRAINB-12845 & CRRAINB-50934 & CRRAINB-51108
                url += "?reason=reject"; //add query para reject
            }
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] releaseCall DELETE " + url);

            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) {
                // don't read getErrorInfo here: it would generate error "body stream already read":
                // it can only be called once on the response data(it is already called in the catch block above)
                throw (response);
            }

            // Update call status
            call.setStatus(CallStatus.ENDED);
            call.startDate = null;
            call.setIsVm(false);
            this.logger.info("[TelephonyServiceSipWise] [TEL][API] releaseCall " + call.id + " - success");

            // Send call update event
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);

            // Remove the call from calls map
            delete this.telephonyService.calls[call.id];

            return call;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'releaseCall');
            this.logger.error(`[TelephonyServiceSipWise] [TEL][API] ${errorInfo.message}`);

            //clean client call not existing on server
            this.deleteCallIfInvalid(errorInfo.error, call);
            throw errorInfo.error;
        }
    }

    /**
     * deleteCallIfInvalid
     * delete call if not existing on server
     * @param {any} error error
     * @param {Call} call call to remove in client if it does not exist on server
     */
    private deleteCallIfInvalid(error: any, call: CallRB) {
        if (error && error.portal === "voice" && error.errorDetailsCode === 404201 && error.errorDetails === "Specified leg not found" && call) { // Invalid connection identifier
            this.logger.warn("[TelephonyServiceSipWise] deleteCallIfInvalid: delete client call non existing on server: " + call.id);

            call.setStatus(CallStatus.ENDED);
            // Send call update event
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
            // Clean the call array
            if (this.telephonyService.calls[call.id]) {
                delete this.telephonyService.calls[call.id];
            }
        }
    }

    /*************************************************************/
    /*                     ANSWER CALL STUFF                     */
    /*************************************************************/

    /**
     * ANSWER CALL sipWise
     * @param {Call} call the call to answer
     * @returns {Call} the answered call
     */
    public async answerCall(call: CallRB): Promise<CallRB> {
        try {
            this.logger.info("[TelephonyServiceSipWise] answerCall : " + call.id);

            const url = new URL(this.portalURL + "calls/" + encodeURIComponent(call.id) + "/answer");
            const headers = this.authService.getPostHeader();
            const reqData = { //with sipWise legId = connectionId
                legId: call.connectionId
            };
            const body = JSON.stringify(reqData);
            this.logger.info("[TelephonyServiceSipWise] answerCall POST " + url);
            this.logger.info("[TelephonyServiceSipWise] answerCall data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }

            // Update call status
            call.setStatus(CallStatus.ACTIVE);
            this.logger.info("[TelephonyServiceSipWise] answerCall success : " + " Call (" + call + ")");
            // Send call update event
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
            return call;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'answerCall');
            this.logger.error(`[TelephonyServiceSipWise] [TEL][API] answerCall ${errorInfo.message}`);

            this.logger.info("[TelephonyServiceSipWise] answerCall fail : error = " + errorInfo.error.errorCode + " msg = " + errorInfo.error.errorMsg + " details = " + errorInfo.error.errorDetails);
            if (errorInfo.error && errorInfo.error.errorDetails === "Device is not able to answer a call") {
                this.mainService.showGlobalPopupMessage({
                    autoClose: false,
                    okLabel: "close",
                    popupTitle: "warning",
                    popupBody: "deviceDoesntAllowAnswer"
                });
            }
            call.setStatus(CallStatus.ENDED);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                    HOLD CALL STUFF                        */
    /*************************************************************/
    /**
     * HOLD CALL sipWise
     * @param {Call} call the call to hold
     * @returns {Call} the held call
     */
    public async holdCall(call: CallRB): Promise<CallRB> {
        try {
            this.logger.info("[TelephonyServiceSipWise] holdCall : " + call.id);

            const url = new URL(this.portalURL + "calls/" + encodeURIComponent(call.id) + "/hold");
            const headers = this.authService.getPostHeader();
            const reqData = { //with sipWise legId = connectionId
                legId: call.connectionId
            };
            const body = JSON.stringify(reqData);
            this.logger.info("[TelephonyServiceSipWise] holdCall POST " + url);
            this.logger.info("[TelephonyServiceSipWise] holdCall data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] holdCall success : " + " Call (" + call + ")");
            // Update call status
            call.setStatus(CallStatus.HOLD);
            // Send call update event
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
            return call;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'holdCall');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            this.telephonyService.makingCall = false;
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                     RETRIEVE CALL STUFF                   */
    /*************************************************************/
    /**
     * RETRIEVE CALL sipWise
     * @param {Call} call the call to retrieve
     * @returns {Call} the retrieved call
     */
    public async retrieveCall(call: CallRB): Promise<CallRB> {
        try {
            this.logger.info("[TelephonyServiceSipWise] retrieveCall : " + call.id);

            const url = new URL(this.portalURL + "calls/" + encodeURIComponent(call.id) + "/retrieve");
            const headers = this.authService.getPostHeader();
            const reqData = { //with sipWise legId = connectionId
                legId: call.connectionId
            };
            const body = JSON.stringify(reqData);
            this.logger.info("[TelephonyServiceSipWise] retrieveCall POST " + url);
            this.logger.info("[TelephonyServiceSipWise] retrieveCall data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] retrieveCall success : " + " Call (" + call + ")");
            // Update call status
            call.setStatus(CallStatus.ACTIVE);
            // Send call update event
            this.eventService.publish("ON_CALL_UPDATED_EVENT", call);
            return call;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveCall');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                     DEFLECT CALL STUFF                    */
    /*************************************************************/
    /**
     * DEFLECT CALL sipWise
     * @param {Call} call the call to deflect
     * @param {string} destination the destination number to which the call must be deflected
     */
    public async deflectCall(call: CallRB, destination: string): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] deflectCall : " + call.id + " to destination : " + destination);
            //reject if missing destination
            if (!destination) {
                const error = new Error("deflect failure - no destination");
                this.logger.error("[TelephonyServiceSipWise] deflect anomaly: no destination");
                throw error;
            }

            const url = new URL(this.portalURL + "calls/" + encodeURIComponent(call.id) + "/deflect");
            const headers = this.authService.getPostHeader();
            const reqData = {
                destination: destination
            };
            const body = JSON.stringify(reqData);
            this.logger.info("[TelephonyServiceSipWise] deflectCall POST " + url);
            this.logger.info("[TelephonyServiceSipWise] deflectCall data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] deflectCall success : " + " Call (" + call + ")");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deflectCall');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                     TRANSFERT CALL STUFF                  */
    /*************************************************************/
    /**
     * TRANSFERT CALL sipWise
     * @param {CallRB} activeCall the active call to transfer
     * @param {CallRB} heldCall the held call to transfer
     */
    public async transferCall(activeCall: CallRB, heldCall: CallRB): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] transferCall : activeCall : " + activeCall.id + " heldCall : " + heldCall.id);
            //reject if missing destination
            if (!activeCall || !heldCall) {
                const error = new Error("transfer failure - missing activeCall or heldCall");
                this.logger.error("[TelephonyServiceSipWise] transfer anomaly: missing calls ref");
                throw error;
            }

            const url = new URL(this.portalURL + "calls/" + encodeURIComponent(activeCall.id) + "/transfer");
            const headers = this.authService.getPostHeader();
            const reqData = {
                heldCallId: heldCall.id
            };
            const body = JSON.stringify(reqData);
            this.logger.info("[TelephonyServiceSipWise] transferCall POST " + url);
            this.logger.info("[TelephonyServiceSipWise] transferCall data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info("[TelephonyServiceSipWise] transferCall success : " + activeCall.id + "->" + heldCall.id);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'transferCall');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                BLIND TRANSFERT CALL STUFF                 */
    /*************************************************************/
    /**
     * BLIND TRANSFERT CALL sipWise
     * @param {CallRB} activeCall the active call to transfer
     * @param {string} calleeNumber the phone number of the callee
     * @param {string} destination the destination object (used in mix webrtc/pstn transfers)
     */
    public async blindTransferCall(activeCall: CallRB, calleeNumber: string, destination: any): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] blindTransferCall : activeCall : " + activeCall.id);
            //reject if missing destination
            if (!activeCall || (!calleeNumber && !destination)) {
                const error = new Error("blind transfer failure - missing activeCall or callee number");
                this.logger.error("[TelephonyServiceSipWise] blind transfer anomaly: missing calls ref or callee number");
                throw error;
            }

            const url = new URL(this.portalURL + "calls/" + encodeURIComponent(activeCall.id) + "/blind-transfer");
            const headers = this.authService.getPostHeader();
            const reqData: { destination: any; } = { destination: undefined };
            if (calleeNumber) {
                reqData.destination = calleeNumber;
            }
            else if (destination) {
                reqData.destination = destination;
            }
            const body = JSON.stringify(reqData);
            this.logger.info("[TelephonyServiceSipWise] blindTransferCall POST " + url);
            this.logger.info("[TelephonyServiceSipWise] blindTransferCall data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] blindTransferCall success : " + " Call (" + activeCall + ")");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'blindTransferCall');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            if (errorInfo && errorInfo.error && errorInfo.error.errorDetailsCode === 400995) {
                this.mainService.showGlobalPopupMessage({
                    autoClose: false,
                    okLabel: "close",
                    popupTitle: "warning",
                    popupBody: "blindTransferNotPossibleForUser"
                });
            }
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                TRANSFER Routing  STUFF                   */
    /*************************************************************/
    /**
     * TRANSFER routing sipWise
     * Send the transfer routing service request to the server
     * @public
     * @async
     * @param {string} calleeId callee identifier
     * @param {string} addresseeId addressee identifier
     * @param {string} addresseePhoneNumber addressee phone number
     * @returns {Promise<any>} Promise resolved with the trasnfer routing data
     */
    public async transferRouting(calleeId: string, addresseeId: string, addresseePhoneNumber: string): Promise<any> {
        this.logger.info(`[TelephonyServiceSipWise] transfer-routing `);

        if (!calleeId || (!addresseeId && !addresseePhoneNumber)) {
            const error = "wrong parameters";
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'transferRouting');
            this.logger.error(`[telephonyBlindTransfer] ${errorInfo.message}`);
            throw errorInfo.error;
        }
        try {
            const url = new URL(this.portalURL + "transfer-routing");

            url.searchParams.set("calleeId", calleeId);
            if (addresseeId) {
                this.logger.info("[TelephonyServiceSipWise] transferRouting with addresseeId " + addresseeId);
                url.searchParams.set("addresseeId", addresseeId);
            }
            if (addresseePhoneNumber) {
                this.logger.info("[TelephonyServiceSipWise] transferRouting with addresseePhoneNumber " + addresseePhoneNumber);
                url.searchParams.set("addresseePhoneNumber", addresseePhoneNumber);
            }

            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] transfer-routing GET " + url);

            const response: Response = await fetch(url.toString(), { method: "GET", headers });
            if (!response.ok) {
                this.logger.debug(`[TelephonyServiceSipWise] transfer-routing -- FAIL -- `);
                const errorInfo = await this.errorHelperService.getErrorInfo(response, 'transfer-routing');
                return errorInfo;
            }
            const transferData = (await response.json()).data;
            this.logger.debug(`[TelephonyServiceSipWise] transfer-routing -- SUCCESS -- `);
            return transferData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'transfer-routing');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                     SEND DTMF STUFF                       */
    /*************************************************************/
    /**
     * send DTMF sipWise
     * @param {CallRB} call the call for which the dtmf must be sent
     * @param {string} dtmf the dtmf digits to send
     */
    public async sendDtmf(call: CallRB, dtmf: string): Promise<void> {
        try {
            this.logger.info("[TelephonyServiceSipWise] sendDtmf for callId : " + call.id + " dtmf : " + dtmf);

            const url = new URL(this.portalURL + "calls/" + encodeURIComponent(call.id) + "/sendDtmf");
            const headers = this.authService.getPostHeader();
            const reqData = {
                legId: call.connectionId,
                digits: dtmf
            };
            const body = JSON.stringify(reqData);
            this.logger.info("[TelephonyServiceSipWise] sendDtmf POST " + url);
            this.logger.info("[TelephonyServiceSipWise] sendDtmf data: " + body);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body });
            if (!response.ok) {
                throw (response);
            }

            this.logger.info("[TelephonyServiceSipWise] sendDtmf success : " + " Call (" + call + ")");
            this.eventService.publish("ON_DTMF_SENT", dtmf);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sendDtmf');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*************************************************************/
    /*                     CLI STUFF                       */
    /*************************************************************/
    // TODO: Use src/models/admin/user.js's model when it will be refactored as typescript class

    public async sipWiseGetCliOption() {
        try {
            const headers = this.authService.getPostHeader();
            const reqMethod = "GET";
            const reqUrl = this.portalURL + "cli-options";
            this.logger.info("[callRouting] Get cli-options " + reqMethod + " " + reqUrl);

            const response: Response = await fetch(reqUrl, { method: reqMethod, headers });
            if (!response.ok) { throw response; }
            const cliOption = (await response.json()).data;
            this.logger.debug(`[callRouting] Get cli-options -- SUCCESS -- `);
            return cliOption;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'cli-options');
            this.logger.error(`[callRouting] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }//end sipWiseGetCliOption

    public async sipWiseSetCliOption(cliOption: any) {

        if (!cliOption || !cliOption.policy) {
            const error = "wrong parameters";
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sipWiseSetCliOption');
            this.logger.error(`[callRouting] ${errorInfo.message}`);
            throw errorInfo.error;
        }
        try {
            const headers = this.authService.getPostHeader();
            const reqMethod = "PUT";
            const reqUrl = this.portalURL + "cli-options";
            this.logger.info("[callRouting] Set cli-options " + reqMethod + " " + reqUrl);

            const body = JSON.stringify(cliOption);
            const response: Response = await fetch(reqUrl, { method: reqMethod, headers, body });
            if (!response.ok) { throw response; }
            const newCliOption = (await response.json()).data;
            this.logger.debug(`[callRouting] Set cli-options -- SUCCESS -- `);
            return newCliOption;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'cli-options');
            this.logger.error(`[callRouting] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }//end sipWiseSetCliOption

    public async sipWiseGetClirOption() {
        try {
            const headers = this.authService.getPostHeader();
            const reqMethod = "GET";
            const reqUrl = this.portalURL + "clir-options";
            this.logger.info("[callRouting] Get clir-options " + reqMethod + " " + reqUrl);

            const response: Response = await fetch(reqUrl, { method: reqMethod, headers });
            if (!response.ok) { throw response; }
            const clirActivated = (await response.json()).data.anonymous;
            this.logger.debug(`[callRouting] Get clir-options -- SUCCESS -- `);
            return clirActivated;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'clir-options');
            this.logger.error(`[callRouting] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }//end sipWiseGetClirOption

    public async sipWiseSetClirOption(clirActivation: boolean) {

        try {
            const headers = this.authService.getPostHeader();
            const reqMethod = "PUT";
            const reqUrl = this.portalURL + "clir-options";
            this.logger.info("[callRouting] Set clir-options " + reqMethod + " " + reqUrl);

            const body = JSON.stringify({ anonymous: clirActivation });
            const response: Response = await fetch(reqUrl, { method: reqMethod, headers, body });
            if (!response.ok) { throw response; }
            const newClirOption = (await response.json()).data;
            this.logger.debug(`[callRouting] Set clir-options -- SUCCESS -- `);
            return newClirOption;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'clir-options');
            this.logger.error(`[callRouting] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }//end sipWiseSetClirOption

    /************************* Voicemail language *************************/
    /**
     * get list of supported voicemail audio languages: de, en, fr, nl, es, it, ro, ar, he...
     * @public
     * @function
     * @async
     * @returns {Promise<any[]>} Status promise resolved with the list of supported languages by voicemail
     */
    public async getVoicemailSupportedLanguages(): Promise<any[]> {

        this.logger.info("[TelephonyServiceSipWise] getVoicemailSupportedLanguages");

        try {
            const headers = this.authService.getRequestHeader();
            const reqUrl = this.portalURL + "messages/supported-languages";

            const response: Response = await fetch(reqUrl, { method: "GET", headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            return responseBody.data.sort(this.languageComparator.bind(this));
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getVoicemailSupportedLanguages');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private languageComparator(language1: any, language2: any): number {
        const uiLanguages: any[] = this.settingsService.getAvailableLanguages();

        let indexLanguage1: number = uiLanguages.findIndex((uiLanguage: any) => { return uiLanguage.codeMoment === language1.code; });
        if (indexLanguage1 === -1) { indexLanguage1 = 100; }
        let indexLanguage2: number = uiLanguages.findIndex((uiLanguage: any) => { return uiLanguage.codeMoment === language2.code; });
        if (indexLanguage2 === -1) { indexLanguage2 = 100; }
        return indexLanguage1 - indexLanguage2;
    }

    /**
     * get current voicemail language of logged user, among same_as_company, de, en, fr, nl, es, it, ro, ar, he...
     * @public
     * @function
     * @async
     * @returns {Promise<string>} Status promise resolved with the current voicemail language
     */
    public async getVoicemailLanguage(): Promise<string> {

        this.logger.info("[TelephonyServiceSipWise] getVoicemailLanguage");
        try {
            const headers = this.authService.getRequestHeader();
            const reqUrl = this.portalURL + "messages/language";

            const response: Response = await fetch(reqUrl, { method: "GET", headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const voicemailLanguage = responseBody.cloudPbxVoicemailLanguage;
            this.logger.info(`[TelephonyServiceSipWise] getVoicemailLanguage returns ${voicemailLanguage}`);
            return voicemailLanguage;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getVoicemailLanguage');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * modify logged user's voicemail language
     * New language possible values: same_as_company, de, en, fr, nl, es, it, ro, ar, he...
     * @public
     * @function
     * @param {string} voicemailLanguage new voicemail language to set for the logged user
     * @async
     * @returns {Promise<string>} Status promise resolved with the new voicemail language
     */
    public async updateVoicemailLanguage(voicemailLanguage: string): Promise<string> {

        this.logger.info(`[TelephonyServiceSipWise] updateVoicemailLanguage ${voicemailLanguage}`);
        try {
            const headers = this.authService.getPostHeader();
            const reqUrl = this.portalURL + "messages/language";

            const reqData = {
                cloudPbxVoicemailLanguage: voicemailLanguage
            };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(reqUrl, { method: "PUT", headers, body });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            this.logger.info(`[TelephonyServiceSipWise] updateVoicemailLanguage returns success`);
            return responseBody.cloudPbxVoicemailLanguage;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateVoicemailLanguage');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getWelcomeServicesByUser
     */
    public async getWelcomeServicesByUser(groupId?: string): Promise<any[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getWelcomeServices");

            const url = new URL(this.portalURL + "ivrs/welcome-services");
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getWelcomeServicesByUser GET " + url);
            if (groupId) { url.searchParams.set("groupId", groupId); }

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getWelcomeServicesByUser success / status : " + response.statusText);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getWelcomeServicesByUser');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getDetailedWelcomeServices
     * @param welcomeServiceId
     */
    public async getDetailedWelcomeServices(welcomeServiceId: string): Promise<any[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getDetailedWelcomeServices");

            const url = new URL(this.portalURL + "ivrs/welcome-services/" + welcomeServiceId);
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getDetailedWelcomeServices GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getDetailedWelcomeServices success / status : " + response.statusText);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getDetailedWelcomeServices');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * forceWelcomeService
     * @param welcomeServiceId
     * @param mode
     */
    public async forceWelcomeService(welcomeServiceId: string, mode: string): Promise<any[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] forceWelcomeService");

            const url = new URL(this.portalURL + "ivrs/welcome-services/" + welcomeServiceId + "/mode?mode=" + mode);
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] forceWelcomeService POST " + url);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] forceWelcomeService success / status : " + response.statusText);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'forceWelcomeService');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getWelcomeServiceGreetings
     * @param welcomeServiceId
     * @param groupId
     */
    public async getWelcomeServiceGreetings(welcomeServiceId: string, groupId: string): Promise<any[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] getWelcomeServiceGreetings");

            const url = new URL(this.portalURL + "ivrs/welcome-services/" + welcomeServiceId + "/greetings");
            url.searchParams.set("groupId", groupId);
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getWelcomeServiceGreetings GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getWelcomeServiceGreetings success / status : " + response.statusText);
            return respData.greetings;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getWelcomeServiceGreetings');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getWelcomeServiceGreeting
     * @param welcomeServiceId
     * @param greetingId
     */
    public async restoreDefaultWelcomeServiceGreeting(welcomeServiceId: string, greetingId: string, groupId: string): Promise<any[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] RestoreDefaultWelcomeServiceGreeting");

            const url = new URL(this.portalURL + "ivrs/welcome-services/" + welcomeServiceId + "/greetings/" + greetingId + "/restore");
            url.searchParams.set("groupId", groupId);
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] RestoreDefaultWelcomeServiceGreeting POST " + url);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] RestoreDefaultWelcomeServiceGreeting success / status : " + response.statusText);
            return respData.greetings;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'RestoreDefaultWelcomeServiceGreeting');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * ActivateWelcomeServiceGreeting
     * @param welcomeServiceId
     * @param fileId
     */
    public async activateWelcomeServiceGreeting(welcomeServiceId: string, fileId: string, groupId: string): Promise<any[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] ActivateWelcomeServiceGreeting");

            const url = new URL(this.portalURL + "ivrs/welcome-services/" + welcomeServiceId + "/greetings/" + fileId + "/activate");
            url.searchParams.set("groupId", groupId);
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] ActivateWelcomeServiceGreeting POST " + url);

            const response: Response = await fetch(url.toString(), { method: 'POST', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] ActivateWelcomeServiceGreeting success / status : " + response.statusText);
            return respData.greetings;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'ActivateWelcomeServiceGreeting');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * updateNewWelcomeServiceGreeting
     * @param welcomeServiceId
     * @param greetingId
     * @param groupId
     *
     */
    public async updateNewWelcomeServiceGreeting(welcomeServiceId: string, greetingId: string, greetingFile: File, groupId: string, loopPlay?: boolean, description?: string, fileName?: string): Promise<any[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] updateNewWelcomeServiceGreeting");

            const url = new URL(this.portalURL + "ivrs/welcome-services/" + welcomeServiceId + "/greetings/" + greetingId);
            if (groupId) url.searchParams.set("groupId", groupId);
            if (loopPlay !== undefined && loopPlay !== null) url.searchParams.set("loopPlay", loopPlay.toString());
            if (description) url.searchParams.set("description", description);
            if (fileName) url.searchParams.set("fileName", fileName);
            const headers = this.authService.getPostHeader(greetingFile.type);
            this.logger.info("[TelephonyServiceSipWise] updateNewWelcomeServiceGreeting PUT " + url);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body: greetingFile });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] updateNewWelcomeServiceGreeting success / status : " + response.statusText);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateNewWelcomeServiceGreeting');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updateSettingsWelcomeServiceGreeting(welcomeServiceId: string, fileId: string, groupId: string, loopPlay: boolean): Promise<any[]> {
        try {
            this.logger.info("[TelephonyServiceSipWise] updateSettingsWelcomeServiceGreeting");

            const url = new URL(this.portalURL + "ivrs/welcome-services/" + welcomeServiceId + "/greetings/" + fileId + "/settings");
            if (groupId) url.searchParams.set("groupId", groupId);

            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] updateSettingsWelcomeServiceGreeting PUT " + url);
            const reqData = {
                loopPlay: loopPlay
            };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] updateSettingsWelcomeServiceGreeting success / status : " + response.statusText);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateNewWelcomeServiceGreeting');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * modify the calendar
     * @public
     * @function
     * @param {string} calendarId Unique identifier of the calendar
     * @param {any} calendarData all data of the calendar
     * @async
     * @returns {Promise<string>} Status promise resolved with the new voicemail language
     */
    public async updateCalendar(calendarId: string, groupId: string, calendarData: any): Promise<any> {

        this.logger.info(`[TelephonyServiceSipWise] updateCalendar ${calendarId}`);
        try {
            const url = new URL(this.portalURL + "calendars/" + calendarId);
            url.searchParams.set("groupId", groupId);
            const headers = this.authService.getPostHeader();
            this.logger.info("[TelephonyServiceSipWise] updateCalendar PUT " + url);

            const reqData = {
                name: calendarData.name,
                officehours: calendarData.officehours
            };
            const body = JSON.stringify(reqData);

            const response: Response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            this.logger.info(`[TelephonyServiceSipWise] updateCalendar returns success`);
            return responseBody.data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'calendarId');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getCalendar
     */
    public async getCalendar(calendarId: string, groupId: string): Promise<any[]> {
        try {
            this.logger.info(`[TelephonyServiceSipWise] getCalendar  ${calendarId}`);

            const url = new URL(this.portalURL + "calendars/" + calendarId);
            url.searchParams.set("groupId", groupId);
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getCalendar GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getCalendar success / status : " + response.statusText);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getCalendar');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getCalendars
     */
    public async getCalendars(limit?: number, sortField?: string, sortOrder?: number): Promise<any[]> {
        try {
            this.logger.info(`[TelephonyServiceSipWise] getCalendar`);

            const url = new URL(this.portalURL + "calendars");
            if (limit) { url.searchParams.set("limit", String(100)); }
            if (sortField) { url.searchParams.set("sortField", sortField); }
            if (sortOrder) { url.searchParams.set("sortOrder", String(sortOrder)); }
            const headers = this.authService.getRequestHeader();
            this.logger.info("[TelephonyServiceSipWise] getCalendars GET " + url);

            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }

            const responseBody: any = await response.json();
            const respData: any = responseBody.data;

            this.logger.info("[TelephonyServiceSipWise] getCalendars success / status : " + response.statusText);
            return respData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getCalendars');
            this.logger.error(`[TelephonyServiceSipWise] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }
}
